// One confirmation for every "Sign out" in the app.
//
// Each sign-out button keeps its own look and place; it just asks for
// confirmation instead of signing out directly:
//
//   const requestSignOut = useSignOutConfirm(onSignOut)
//   <button onClick={requestSignOut}>…</button>
//
// <SignOutConfirmProvider> (mounted once in App, inside <LanguageProvider>)
// owns the dialog. The dialog is deliberately weighted toward staying:
// "Stay signed in" is the filled, default-focused primary; "Sign out" is the
// quiet destructive secondary. Escape, backdrop click and Cancel all dismiss;
// focus moves into the dialog on open and back to the trigger on close; body
// scroll is locked while open.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from '../../utils/translations'
import './ConfirmSignOut.css'

const SignOutConfirmContext = createContext(null)

/* Returns a click handler that opens the confirmation; `onSignOut` runs only
   if the person confirms. Falls back to signing out directly when no
   provider is mounted (never expected in the app, but keeps tests simple). */
export const useSignOutConfirm = (onSignOut) => {
  const request = useContext(SignOutConfirmContext)
  return useCallback(
    (event) => {
      if (!onSignOut) return
      if (!request) {
        onSignOut()
        return
      }
      request(onSignOut, event?.currentTarget ?? null)
    },
    [request, onSignOut]
  )
}

export const SignOutConfirmProvider = ({ children }) => {
  // pending: { onSignOut, trigger } while the dialog is open, else null.
  const [pending, setPending] = useState(null)

  const request = useCallback((onSignOut, trigger) => {
    setPending({ onSignOut, trigger })
  }, [])

  const close = useCallback(() => {
    setPending((current) => {
      const trigger = current?.trigger
      if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
        // Return focus after React has removed the dialog.
        requestAnimationFrame(() => trigger.focus())
      }
      return null
    })
  }, [])

  const confirm = useCallback(() => {
    const onSignOut = pending?.onSignOut
    setPending(null)
    if (onSignOut) onSignOut()
  }, [pending])

  return (
    <SignOutConfirmContext.Provider value={request}>
      {children}
      {pending && <ConfirmSignOut onCancel={close} onConfirm={confirm} />}
    </SignOutConfirmContext.Provider>
  )
}

SignOutConfirmProvider.propTypes = { children: PropTypes.node }

const ConfirmSignOut = ({ onCancel, onConfirm }) => {
  const { t } = useTranslation()
  const dialogRef = useRef(null)
  const cancelRef = useRef(null)

  // Default focus lands on "Stay signed in"; lock the page behind.
  useEffect(() => {
    cancelRef.current?.focus()
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = overflow
    }
  }, [])

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
      return
    }
    if (event.key !== 'Tab' || !dialogRef.current) return
    // Keep Tab inside the two buttons.
    const focusable = dialogRef.current.querySelectorAll('button:not([disabled])')
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <div
      className="confirm-signout-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel()
      }}
    >
      <div
        ref={dialogRef}
        className="confirm-signout"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-signout-title"
        aria-describedby="confirm-signout-body"
        onKeyDown={handleKeyDown}
      >
        <h2 id="confirm-signout-title" className="confirm-signout-title">
          {t('signOut.title')}
        </h2>
        <p id="confirm-signout-body" className="confirm-signout-body">
          {t('signOut.body')}
        </p>
        <div className="confirm-signout-actions">
          <button
            ref={cancelRef}
            type="button"
            className="confirm-signout-stay"
            onClick={onCancel}
          >
            {t('signOut.cancel')}
          </button>
          <button type="button" className="confirm-signout-leave" onClick={onConfirm}>
            {t('signOut.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}

ConfirmSignOut.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
}

export default ConfirmSignOut
